import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Link } from "react-router-dom";
import { Check, Lock, ArrowRight, Megaphone, Target, TrendingUp } from "lucide-react";
import { SprintDoneCard } from "@/components/SprintDoneCard";

const STAGES = ["STUCK","EXPLORING","CHOSEN_A_PATH","STARTED","FIRST_100","FIRST_1000","PCT_25","PCT_50","PCT_75","PCT_100","EXIT_READY"];
const LABELS = {
  STUCK:"Stuck", EXPLORING:"Exploring", CHOSEN_A_PATH:"Chosen a path", STARTED:"Started",
  FIRST_100:"First $100", FIRST_1000:"First $1,000",
  PCT_25:"25% replaced", PCT_50:"50% replaced", PCT_75:"75% replaced",
  PCT_100:"100% replaced", EXIT_READY:"Exit ready",
};

export default function Plan() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [chosen, setChosen] = useState(null);
  const [opp, setOpp] = useState(null);
  const [sprint, setSprint] = useState({});
  const [exit, setExit] = useState(null);
  const [stage, setStage] = useState("STUCK");
  const [streak, setStreak] = useState({ current_streak: 0 });

  useEffect(() => {
    (async () => {
      try {
        const pr = await api.get("/progress");
        const path = pr.data.progress?.chosen_path || null;
        setChosen(path);
        if (path) {
          const [o, s, ex, me, st] = await Promise.all([
            api.get(`/opportunities/${path}`).catch(() => null),
            api.get(`/sprint/${path}`).catch(() => null),
            api.get("/exit").catch(() => null),
            api.get("/auth/me").catch(() => null),
            api.get("/streak").catch(() => null),
          ]);
          setOpp(o?.data.opportunity || null);
          setSprint(s?.data.sprint || {});
          setExit(ex?.data.exit || null);
          setStage(me?.data.user?.roadmap_stage || "STUCK");
          setStreak(st?.data || { current_streak: 0 });
        }
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-8 text-gray-400" data-testid="plan-loading">Loading your plan…</div>;

  // ---- Empty state: no committed path ----
  if (!chosen || !opp) {
    return (
      <div className="max-w-md mx-auto px-5 pt-16 text-center" data-testid="plan-empty">
        <div className="eyebrow text-[#FD0F10]">YOUR PLAN</div>
        <h1 className="font-display text-3xl font-black mt-2">Pick your path first</h1>
        <p className="text-gray-300 mt-3 text-sm">Once you choose a path, your whole escape plan shows up here in one place.</p>
        <Link to="/dashboard" className="mt-6 inline-flex items-center gap-2 hero-accent text-white font-bold px-6 py-3 rounded" data-testid="plan-pick-path-btn">
          See my paths <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  const isPremium = !!user?.is_premium;
  const titles = opp.seven_day_titles || [];
  const freeLimit = 7;
  const hasLocked = false;

  const isDone = (n) => !!sprint[`day_${n}`];
  const isLocked = (n) => n > freeLimit;
  let currentDay = null;
  for (let n = 1; n <= 7; n++) {
    if (!isDone(n) && !isLocked(n)) { currentDay = n; break; }
  }
  const allDone = [1, 2, 3, 4, 5, 6, 7].every((n) => isDone(n));

  const dayLabel = (n) => {
    if (isDone(n)) return "done";
    if (isLocked(n)) return "locked";
    if (n === currentDay) return "current";
    return "upcoming";
  };

  // ---- Exit targets ----
  const exitNumber = exit?.monthly_expenses;
  const sideTarget = exit?.side_income_target;
  const hasTargets = exit && (exitNumber != null || sideTarget != null);

  const stageLabel = LABELS[stage] || "Stuck";

  return (
    <div className="max-w-2xl mx-auto px-5 pt-6 pb-8" data-testid="plan-page">
      {/* 1. Header */}
      <div className="eyebrow text-[#FD0F10]">YOUR PLAN</div>
      <h1 className="font-display text-3xl sm:text-4xl font-black mt-1 leading-tight" data-testid="plan-header-path">{opp.title}</h1>
      <p className="text-gray-200 text-base mt-2">Here's your plan to get out.</p>

      {/* 2. Your Next Step */}
      <section className="mt-6 card-panel-elevated p-5 border-[#FD0F10]/40" data-testid="plan-next-step">
        <div className="eyebrow text-[#FD0F10]">YOUR NEXT STEP</div>
        {currentDay ? (
          <>
            <div className="mt-2 font-mono-ui text-[10px] uppercase tracking-wider text-gray-400">Day {currentDay}</div>
            <div className="font-display text-xl font-black text-white mt-0.5" data-testid="plan-next-step-title">{titles[currentDay - 1]}</div>
            <Link
              to={`/opportunity/${chosen}#sprint-day-row-${currentDay}`}
              className="mt-4 inline-flex items-center gap-2 hero-accent text-white font-bold px-5 py-3 rounded"
              data-testid="plan-next-step-btn"
            >
              Open this step <ArrowRight size={16} />
            </Link>
          </>
        ) : hasLocked ? (
          <>
            <div className="font-display text-xl font-black text-white mt-2">You've done your free days.</div>
            <p className="text-sm text-gray-300 mt-1">Unlock the full plan with Pro to keep going, one clear move at a time.</p>
            <Link to="/pro" className="mt-4 inline-flex items-center gap-2 hero-accent text-white font-bold px-5 py-3 rounded" data-testid="plan-next-step-btn">
              Unlock the full plan <ArrowRight size={16} />
            </Link>
          </>
        ) : allDone ? (
          <SprintDoneCard isPremium={isPremium} />
        ) : (
          <>
            <div className="font-display text-xl font-black text-white mt-2">Start Day 1.</div>
            <Link to={`/opportunity/${chosen}#sprint-day-row-1`} className="mt-4 inline-flex items-center gap-2 hero-accent text-white font-bold px-5 py-3 rounded" data-testid="plan-next-step-btn">
              Open this step <ArrowRight size={16} />
            </Link>
          </>
        )}
      </section>

      {/* 3. Your Launch Checklist */}
      <section className="mt-5 card-panel p-5" data-testid="plan-checklist">
        <div className="eyebrow text-[#FD0F10]">YOUR LAUNCH CHECKLIST</div>
        <p className="text-sm text-gray-400 mt-1">Seven days to launch. One move a day.</p>
        <ol className="mt-4 space-y-2">
          {[1, 2, 3, 4, 5, 6, 7].map((n) => {
            const status = dayLabel(n);
            return (
              <li
                key={n}
                className={`flex items-start gap-3 p-3 rounded border bg-[#0B0C0E] ${status === "current" ? "border-[#FD0F10]/60" : "border-[#2A2E39]"}`}
                data-testid={`plan-day-${n}`}
              >
                <div className="mt-0.5 shrink-0">
                  {status === "done" ? (
                    <div className="w-6 h-6 rounded-full hero-accent flex items-center justify-center"><Check size={14} className="text-white" /></div>
                  ) : status === "locked" ? (
                    <div className="w-6 h-6 rounded-full bg-[#14161B] border border-[#2A2E39] flex items-center justify-center"><Lock size={13} className="text-gray-400" /></div>
                  ) : (
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center font-mono-ui text-xs font-bold ${status === "current" ? "border border-[#FD0F10] text-[#FD0F10]" : "bg-[#14161B] border border-[#2A2E39] text-gray-400"}`}>{n}</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono-ui text-[10px] uppercase tracking-wider flex items-center gap-2">
                    <span className="text-gray-400">Day {n}</span>
                    {status === "done" && <span className="text-[#FD0F10]" data-testid={`plan-day-${n}-status`}>Done</span>}
                    {status === "current" && <span className="px-1.5 py-0.5 border border-[#FD0F10] rounded text-[9px] text-[#FD0F10]" data-testid={`plan-day-${n}-status`}>Current</span>}
                    {status === "locked" && <span className="text-gray-500" data-testid={`plan-day-${n}-status`}>Locked</span>}
                  </div>
                  <div className={`text-sm font-semibold mt-0.5 ${status === "locked" ? "text-gray-400" : "text-white"}`}>{titles[n - 1]}</div>
                </div>
              </li>
            );
          })}
        </ol>
        {hasLocked && (
          <Link to="/pro" className="mt-4 flex items-center justify-center gap-2 hero-accent text-white font-bold px-4 py-3 rounded" data-testid="plan-upgrade-btn">
            <Lock size={15} /> Unlock the full plan with Pro
          </Link>
        )}
      </section>

      {/* 4. Your Marketing */}
      <section className="mt-5 card-panel p-5" data-testid="plan-marketing">
        <div className="eyebrow text-[#FD0F10] flex items-center gap-2">
          YOUR MARKETING
          {!isPremium && <Lock size={12} className="text-gray-400" data-testid="plan-marketing-lock" />}
        </div>
        <div className="mt-2 flex items-start gap-3">
          <Megaphone size={20} className="text-[#FD0F10] mt-0.5 shrink-0" />
          <p className="text-sm text-gray-200 flex-1">Get captions, hashtags and post ideas from your coach.</p>
        </div>
        <Link to="/coach" className="mt-4 inline-flex items-center gap-2 border border-[#2A2E39] hover:border-white text-white font-bold px-5 py-3 rounded" data-testid="plan-marketing-btn">
          Open your coach <ArrowRight size={16} />
        </Link>
      </section>

      {/* 5. Your Targets */}
      <section className="mt-5 card-panel p-5" data-testid="plan-targets">
        <div className="eyebrow text-[#FD0F10] flex items-center gap-2"><Target size={14} /> YOUR TARGETS</div>
        {hasTargets ? (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="bg-[#0B0C0E] border border-[#2A2E39] rounded p-3">
              <div className="font-mono-ui text-[9px] uppercase tracking-wider text-gray-400">Monthly income you need to quit</div>
              <div className="font-display font-black text-2xl text-white mt-1" data-testid="plan-exit-number">{exitNumber != null ? `$${Number(exitNumber).toLocaleString()}` : "Not set"}</div>
            </div>
            <div className="bg-[#0B0C0E] border border-[#2A2E39] rounded p-3">
              <div className="font-mono-ui text-[9px] uppercase tracking-wider text-gray-400">Side income target (per month)</div>
              <div className="font-display font-black text-2xl text-white mt-1" data-testid="plan-side-target">{sideTarget != null ? `$${Number(sideTarget).toLocaleString()}` : "Not set"}</div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-300 mt-2">You haven't set your numbers yet. The calculator shows your finish line.</p>
        )}
        <Link to="/exit" className="mt-4 inline-flex items-center gap-2 border border-[#2A2E39] hover:border-white text-white font-bold px-5 py-3 rounded" data-testid="plan-targets-btn">
          {hasTargets ? "Edit in calculator" : "Set my targets"} <ArrowRight size={16} />
        </Link>
      </section>

      {/* 6. Your Progress */}
      <section className="mt-5 card-panel p-5" data-testid="plan-progress">
        <div className="eyebrow text-[#FD0F10] flex items-center gap-2"><TrendingUp size={14} /> YOUR PROGRESS</div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="bg-[#0B0C0E] border border-[#2A2E39] rounded p-3">
            <div className="font-mono-ui text-[9px] uppercase tracking-wider text-gray-400">Where you are</div>
            <div className="font-display font-black text-xl text-white mt-1" data-testid="plan-stage">{stageLabel}</div>
          </div>
          <div className="bg-[#0B0C0E] border border-[#2A2E39] rounded p-3">
            <div className="font-mono-ui text-[9px] uppercase tracking-wider text-gray-400">Weekly check-in streak</div>
            <div className="font-display font-black text-xl text-white mt-1" data-testid="plan-streak">{streak.current_streak || 0} {Number(streak.current_streak) === 1 ? "week" : "weeks"}</div>
          </div>
        </div>
        <Link to="/checkin" className="mt-4 inline-flex items-center gap-2 border border-[#2A2E39] hover:border-white text-white font-bold px-5 py-3 rounded" data-testid="plan-checkin-btn">
          Log this week's check-in <ArrowRight size={16} />
        </Link>
      </section>
    </div>
  );
}
